/* eslint-disable no-prototype-builtins */
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { SessionCache } from "../helpers/cache";
import { NullableString } from "../core/models/ModelHelpers";
import { EVENT_ENDPOINT_TIMEOUT, EVENT_FORCE_ROUTE_CHANGE } from "../Events";
import IHeaders from "../core/contracts/IHeaders";
import Paginate from "../core/models/Paginate";
import winston from "winston";
import LogzioWinstonTransport from "winston-logzio";
import Hubx from "../HubX";
import User from "../core/models/User";
import { APP_DEFAULT_API_URL, APP_DEFAULT_ENV_STR } from "../core/constants/appGlobals";

export default class BaseService extends Hubx {
  api: AxiosInstance;
  bearerToken: NullableString;
  baseURL: string;
  radixAPI: boolean;
  log: boolean;

  constructor(radixAPI = false, responseTypeFile = false, log = true) {
    super();
    this.radixAPI = radixAPI;
    this.bearerToken = this.getToken();
    this.log = log;
    this.baseURL = this.getBaseURL();

    const headers = radixAPI
      ? {
          authorization: this.bearerToken ? `Bearer ${this.bearerToken}` : "",
        }
      : {
          "Apikey": process.env.REACT_APP_API_KEY,
          "Accept": "*/*"
        };

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: headers,
      responseType: responseTypeFile ? "arraybuffer" : "json",
    });
  }

  private getBaseURL(): string {
    const currentURL = window.location.hostname;
    const isProd = currentURL == "wmgerenciar.whitemartins.com.br";
    return this.radixAPI
      ? this.getBaseURLAndEnvironment()[0]
      : isProd
      ? "https://apitelemetria.whitemartins.com.br"
      : "https://apitelemetriahml.whitemartins.com.br/gerenciar";
  }

  private getBaseURLAndEnvironment(): string[] {
    const cache = new SessionCache();
    const cachedUser: User | null = cache.get("user");

    return [
      process.env.REACT_APP_API_URL ?? APP_DEFAULT_API_URL,
      process.env.REACT_APP_LOG_ENV ?? APP_DEFAULT_ENV_STR,
      cachedUser?.username ?? "",
    ];
  }

  private getToken(): NullableString {
    const sessionStorage = new SessionCache();

    const cachedBearer: NullableString = sessionStorage.get<NullableString>("bearer-token");
    return cachedBearer;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public logAndShip(level: string, message: string, response?: object): void {
    const baseURLAndEnvironment = this.getBaseURLAndEnvironment();
    const environment = baseURLAndEnvironment[1];
    const loggedUser = baseURLAndEnvironment[2];

    if (process.env.NODE_ENV !== "development") {
      const logzioWinstonTransport = new LogzioWinstonTransport({
        name: "winston_logzio",
        token: "pgOgkadjlDZEgwOzIrlQSMUgoZVIZdvE",
        host: "listener.logz.io",
        protocol: "https",
      });

      const logger = winston.createLogger({
        format: winston.format.simple(),
        transports: [logzioWinstonTransport],
      });

      if (response) {
        logger.log(level, message, {
          environment: environment,
          loggedUser: loggedUser,
          ...response,
        });
      } else {
        logger.log(level, message, { environment: environment, loggedUser: loggedUser });
      }
      logger.close();
    }
  }

  async get<T>(url: string): Promise<T> {
    try {
      const results = await this.api.get(url);
      if (
        this.baseURL.indexOf("whitemartins") > 0 &&
        results.data.hasOwnProperty("originalError")
      ) {
        this.logAndShip("warn", "BaseService -> Post ->", results);

        throw { response: results.data };
      }
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async getPaginated<T>(url: string): Promise<Paginate<T>> {
    try {
      const results = await this.api.get(url);
      const APIResponse = this.handleResponse<T[]>(results);
      const headers = this.handleHeaders<T>(results);
      const pagination = JSON.parse(headers.pagination || "");

      const response: Paginate<T> = {
        items: APIResponse,
        pagination,
      };

      return response;
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async delete<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.delete(url, { data: params });
      if (
        this.baseURL.indexOf("whitemartins") > 0 &&
        results.data.hasOwnProperty("originalError")
      ) {
        this.logAndShip("warn", "BaseService -> Post ->", results);
        throw { response: results.data };
      }
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async put<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.put(url, params);
      if (
        this.baseURL.indexOf("whitemartins") > 0 &&
        results.data.hasOwnProperty("originalError")
      ) {
        this.logAndShip("warn", "BaseService -> Post ->", results);
        throw { response: results.data };
      }
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async post<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.post(url, params);
      if (
        this.baseURL.indexOf("whitemartins") > 0 &&
        results?.data?.hasOwnProperty("originalError")
      ) {
        this.logAndShip("warn", "BaseService -> Post ->", results);
        throw { response: results.data };
      }
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  private handleResponse<T>(APIResponse: AxiosResponse): T {
    return APIResponse.data;
  }

  private handleHeaders<T>(APIResponse: AxiosResponse): IHeaders {
    return APIResponse.headers;
  }

  private clearCacheAndReroute() {
    const cache = new SessionCache();
    cache.clear();
    window?.Hubx?.notify(EVENT_FORCE_ROUTE_CHANGE, "/login/");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleRequestError(APIResponse: any) {
    console.log("APIResponse", APIResponse);
    // eslint-disable-next-line no-console
    console.warn("BaseService -> handleRequestError ->", APIResponse);

    if (this.log) this.logAndShip("warn", "BaseService -> handleRequestError ->", APIResponse);

    if (APIResponse.code == "ETIMEOUT" || APIResponse.code == "EREQUEST") {
      window?.Hubx?.notify(EVENT_ENDPOINT_TIMEOUT, true);
    }

    if (this.radixAPI && (APIResponse?.code === 401 || APIResponse?.status === 401)) {
      this.clearCacheAndReroute();
    }

    // expired password
    if (this.radixAPI && (APIResponse?.code === 417 || APIResponse?.status === 417)) {
      const hasToken = Boolean(APIResponse?.data?.token);

      window.location.replace(
        hasToken ? `/reset/${APIResponse?.data?.token}/true` : "/forgot_password/"
      );
    }

    return Promise.reject(APIResponse);
  }
}
